import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminOrInstitutionAdminRoute from "./routes/AdminOrInstitutionAdminRoute";
import InstitutionAdminRoute from "./routes/InstitutionAdminRoute";
import AdminRoute from "./routes/AdminRoute";
import AppShell from "./layout/AppShell";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import "./styles/lawAfricaBrand.css";
import "./styles/adminGlobal.css";

const AdminTocManager = lazy(() => import("./pages/dashboard/admin/AdminTocManager"));
const AdminCourts = lazy(() => import("./pages/dashboard/admin/AdminCourts"));
const CommentaryPlayground = lazy(() => import("./pages/CommentaryPlayground"));
const InsightAiAccess = lazy(() => import("./pages/dashboard/InsightAiAccess"));
const AdminLegalAiEconomics = lazy(() => import("./pages/dashboard/admin/AdminLegalAiEconomics"));
const AdminLLRImport = lazy(() => import("./pages/dashboard/admin/AdminLLRImport"));
const Register = lazy(() => import("./pages/Register"));
const TwoFactor = lazy(() => import("./pages/TwoFactor"));
const TwoFactorSetup = lazy(() => import("./pages/TwoFactorSetup"));
const PaystackReturn = lazy(() => import("./pages/payments/PaystackReturn"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInstitutionInvitation = lazy(() => import("./pages/AcceptInstitutionInvitation"));
const SecurityDashboard = lazy(() => import("./pages/dashboard/SecurityDashboard"));
const InstitutionApprovalDashboard = lazy(() => import("./pages/dashboard/InstitutionApprovalDashboard"));
const AdminReportContent = lazy(() => import("./pages/dashboard/admin/AdminReportContent"));
const AdminInstitutions = lazy(() => import("./pages/dashboard/admin/AdminInstitutions"));
const AdminContentProducts = lazy(() => import("./pages/dashboard/admin/AdminContentProducts"));
const AdminInstitutionAdmins = lazy(() => import("./pages/dashboard/admin/AdminInstitutionAdmins"));
const AdminInstitutionSubscriptions = lazy(() => import("./pages/dashboard/admin/AdminInstitutionSubscriptions"));
const AdminInstitutionBundleSubscriptions = lazy(() => import("./pages/dashboard/admin/AdminInstitutionBundleSubscriptions"));
const AdminProductDocuments = lazy(() => import("./pages/dashboard/admin/AdminProductDocuments"));
const AdminDocuments = lazy(() => import("./pages/dashboard/admin/AdminDocuments"));
const AdminTextIndexing = lazy(() => import("./pages/dashboard/admin/AdminTextIndexing"));
const AdminInstitutionUsers = lazy(() => import("./pages/dashboard/admin/AdminInstitutionUsers"));
const AdminUsers = lazy(() => import("./pages/dashboard/admin/AdminUsers"));
const AdminLLRServices = lazy(() => import("./pages/dashboard/admin/AdminLLRServices"));
const InstitutionMembersAdmin = lazy(() => import("./pages/dashboard/institution/InstitutionMembersAdmin"));
const InstitutionUsers = lazy(() => import("./pages/dashboard/institution/InstitutionUsers"));
const InstitutionUserImport = lazy(() => import("./pages/dashboard/institution/InstitutionUserImport"));
const InstitutionAnalytics = lazy(() => import("./pages/dashboard/institution/InstitutionAnalytics"));
const InstitutionLegalAiAnalytics = lazy(() => import("./pages/dashboard/institution/InstitutionLegalAiAnalytics"));
const AdminSubscriptionRequests = lazy(() => import("./pages/dashboard/approvals/AdminSubscriptionRequests"));
const DevContentBlocksTest = lazy(() => import("./pages/dashboard/DevContentBlocksTest"));
const AdminDocumentCategories = lazy(() => import("./pages/dashboard/admin/AdminDocumentCategories"));
const AdminDocumentSubCategories = lazy(() => import("./pages/dashboard/admin/AdminDocumentSubCategories"));
const Explore = lazy(() => import("./pages/dashboard/Explore"));
const Library = lazy(() => import("./pages/dashboard/Library"));
const AdminTrials = lazy(() => import("./pages/dashboard/admin/AdminTrials"));
const GlobalAdminHomeSwitch = lazy(() => import("./pages/dashboard/GlobalAdminHomeSwitch"));
const DocumentDetails = lazy(() => import("./pages/documents/DocumentDetails"));
const AdminInvoices = lazy(() => import("./pages/dashboard/admin/AdminInvoices"));
const AdminInvoiceDetail = lazy(() => import("./pages/dashboard/admin/AdminInvoiceDetail"));
const AdminInvoiceSettings = lazy(() => import("./pages/dashboard/admin/AdminInvoiceSettings"));
const AdminPayments = lazy(() => import("./pages/dashboard/admin/AdminPayments"));
const AdminUserSubscriptions = lazy(() => import("./pages/dashboard/admin/AdminUserSubscriptions"));
const AdminContentProductPrices = lazy(() => import("./pages/dashboard/admin/AdminContentProductPrices"));
const AdminVATRates = lazy(() => import("./pages/dashboard/admin/AdminVatRates"));
const DocumentReader = lazy(() => import("./pages/documents/DocumentReader"));
const AdminWorkspace = lazy(() => import("./pages/dashboard/admin/AdminWorkspace"));

function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: "38vh",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        color: "#66707a",
        fontWeight: 700,
        fontSize: "13px",
      }}
    >
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* ===================== */}
          {/* PUBLIC ROUTES */}
          {/* ===================== */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/explore" element={<Explore publicMode />} />
          <Route path="/catalogue" element={<Navigate to="/explore" replace />} />
          <Route path="/login" element={<Login />} />

          {/* ✅ Reset password (support both with/without trailing slash) */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/" element={<ResetPassword />} />

          {/* Institution invitation activation is deliberately public. */}
          <Route path="/accept-invitation" element={<AcceptInstitutionInvitation />} />
          <Route path="/accept-invitation/" element={<AcceptInstitutionInvitation />} />

          <Route path="/register" element={<Register />} />
          <Route path="/twofactor" element={<TwoFactor />} />
          <Route path="/twofactor-setup" element={<TwoFactorSetup />} />

          {/* ✅ Paystack redirects here after payment */}
          <Route path="/payments/paystack/return" element={<PaystackReturn />} />

          {/* Law Reports are intentionally hidden from public/end-user surfaces for now. */}
          <Route path="/law-reports/*" element={<Navigate to="/explore" replace />} />
          <Route path="/reports/*" element={<Navigate to="/explore" replace />} />

          {/* ===================== */}
          {/* PROTECTED APP */}
          {/* ===================== */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Base */}
            <Route index element={<GlobalAdminHomeSwitch />} />

            <Route path="explore" element={<Explore />} />
            <Route path="library" element={<Library />} />
            {/* Law Profiles are deferred. Keep old bookmarks harmless. */}
            <Route path="lawyers/*" element={<Navigate to="/dashboard" replace />} />

            {/* Legal AI */}
            <Route path="ai/commentary" element={<CommentaryPlayground />} />
            <Route path="ai/access" element={<InsightAiAccess />} />

            {/* Law Reports are retained internally but hidden from end users until redesign. */}
            <Route path="law-reports/*" element={<Navigate to="/dashboard/explore" replace />} />
            <Route path="trials" element={<Navigate to="/dashboard/explore" replace />} />
            <Route path="security" element={<SecurityDashboard />} />

            {/* ===================== */}
            {/* INSTITUTION ADMIN */}
            {/* ===================== */}
            <Route element={<InstitutionAdminRoute />}>
              <Route path="institution/users" element={<InstitutionUsers />} />
              <Route path="institution/import" element={<InstitutionUserImport />} />
              <Route path="institution/analytics" element={<InstitutionAnalytics />} />
            </Route>

            {/* ===================== */}
            {/* APPROVALS */}
            {/* ===================== */}
            <Route element={<AdminOrInstitutionAdminRoute />}>
              <Route path="approvals" element={<InstitutionApprovalDashboard />} />
              <Route path="approvals/subscription-requests" element={<AdminSubscriptionRequests />} />
              <Route path="approvals/members" element={<InstitutionMembersAdmin />} />
              <Route path="approvals/legal-ai" element={<InstitutionLegalAiAnalytics />} />
            </Route>

            {/* ===================== */}
            {/* ADMIN */}
            {/* ===================== */}
            <Route element={<AdminRoute />}>
              <Route path="admin" element={<AdminWorkspace />} />
              <Route path="admin/legal-ai" element={<AdminLegalAiEconomics />} />
              <Route path="admin/institutions" element={<AdminInstitutions />} />
              <Route path="admin/content-products" element={<AdminContentProducts />} />
              <Route path="admin/content-product-prices" element={<AdminContentProductPrices />} />
              <Route path="admin/document-categories" element={<AdminDocumentCategories />} />
              <Route path="admin/document-subcategories" element={<AdminDocumentSubCategories />} />
              <Route path="admin/documents" element={<AdminDocuments />} />
              <Route path="admin/text-indexing" element={<AdminTextIndexing />} />
              <Route path="admin/documents/:id/toc" element={<AdminTocManager />} />
              <Route path="admin/toc-test" element={<Navigate to="/dashboard/admin/documents" replace />} />
              <Route path="admin/llr-services" element={<AdminLLRServices />} />
              <Route path="admin/courts" element={<AdminCourts />} />
              {/* Law Profiles are deferred. */}
              <Route path="admin/lawyers/*" element={<Navigate to="/dashboard/admin" replace />} />
              <Route path="admin/llr-services/import" element={<AdminLLRImport />} />
              <Route path="/dashboard/dev/content-blocks" element={<DevContentBlocksTest />} />
              <Route path="admin/llr-services/:legalDocumentId/content" element={<AdminReportContent />} />

              <Route path="admin/institution-subscriptions" element={<AdminInstitutionSubscriptions />} />
              <Route path="admin/institution-bundle-subscriptions" element={<AdminInstitutionBundleSubscriptions />} />
              <Route path="admin/user-subscriptions" element={<AdminUserSubscriptions />} />
              <Route path="admin/trials" element={<AdminTrials />} />
              <Route path="admin/institution-admins" element={<AdminInstitutionAdmins />} />
              <Route path="admin/content-products/:productId/documents" element={<AdminProductDocuments />} />
              <Route path="admin/institutions/:id/users" element={<AdminInstitutionUsers />} />
              <Route path="admin/users" element={<AdminUsers />} />

              {/* ===================== */}
              {/* ✅ FINANCE (Admin-only) */}
              {/* ===================== */}
              <Route path="admin/finance/invoices" element={<AdminInvoices />} />
              <Route path="admin/finance/invoices/:id" element={<AdminInvoiceDetail />} />
              <Route path="admin/finance/invoice-settings" element={<AdminInvoiceSettings />} />
              <Route path="admin/finance/vat-rates" element={<AdminVATRates />} />
              <Route path="admin/finance/payments" element={<AdminPayments />} />
            </Route>

            {/* Documents */}
            <Route path="documents/:id" element={<DocumentDetails />} />
            <Route path="documents/:id/read" element={<DocumentReader />} />
          </Route>

          {/* ===================== */}
          {/* REDIRECTS */}
          {/* ===================== */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
